"use client";
import React, { useEffect, useState } from "react";
import { IconClock, IconCalendarEvent, IconTag } from "@tabler/icons-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import img from "../assets/noNotifications.png";
import "./notifications.css";
import axios from "axios";
import { REST_API_URL } from "../api/config";

const BASE_URL = REST_API_URL;
const API_ROUTES = {
  getNotificationList: "notification/notifications-list/",
  markViewed: "notification/notification-viewed/",
  userInfo: "auth/userinfo/",
};

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) =>
  CURRENCY_SYMBOL_MAP[code] || code || "";

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function NotificationPanel() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [userCurrencyCode, setUserCurrencyCode] = useState("USD");

  const getToken = () =>
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });

  const redirectToLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/sign-in");
  };

  useEffect(() => {
    const token = getToken();
    if (!token) return redirectToLogin();

    const fetchUserInfo = async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await axios.get(`${BASE_URL}/${API_ROUTES.userInfo}`, {
          headers,
        });

        setUserCurrencyCode(res.data?.currency || "USD");
      } catch (err) {
        console.error("❌ Failed to fetch user info:", err);
      }
    };

    fetchUserInfo();
  }, []);

  const fetchNotifications = async () => {
    const token = getToken();
    if (!token) return redirectToLogin();

    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(`${BASE_URL}/${API_ROUTES.getNotificationList}`, { 
        headers 
      });

      if (res.status === 401) return redirectToLogin();

      const data = await res.json();
      const list = Array.isArray(data?.earlier) ? data.earlier : [];

      const sorted = [...list].sort((a, b) =>
        a.viewed === b.viewed ? 0 : a.viewed ? 1 : -1
      );

      setNotifications(sorted);
    } catch (err) {
      console.error("❌ Error fetching notifications:", err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markNotificationViewed = async (item) => {
    const token = getToken();
    if (!token || !item?.id) return;

    try {
      const headers = buildHeaders(token);
      
      await fetch(`${BASE_URL}${API_ROUTES.markViewed}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: item.id }),
      });

      setNotifications((prev) =>
        prev
          .map((n) =>
            n.id === item.id ? { ...n, viewed: true } : n
          )
          .sort((a, b) =>
            a.viewed === b.viewed ? 0 : a.viewed ? 1 : -1
          )
      );

      setSelectedNotification(item);
    } catch (err) {
      console.error("❌ Error marking viewed:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <div className="notification-panel">
      <div className="notif-header">
        <div className="notif-header-left">
          <h2>Notifications</h2>
          {notifications.length > 0 && (
            <span className="notif-count">
              {notifications.length}
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div className="notif-empty">
          Loading notifications...
        </div>
      )}

      {!loading && notifications.length === 0 && (
        <div className="notif-empty">
          <img src={img} alt="no notifications" />
          <p>No notifications yet</p>
        </div>
      )}

      {!loading && notifications.length > 0 && (
        <div className="notif-list">
          {notifications.map((item) => {
            const info = item.appointment_info || {};
            const currencyCode =
              info.currency || userCurrencyCode;

            const price = info.price
              ? `${getCurrencySymbol(currencyCode)}${info.price}`
              : "";

            return (
              <div
                key={item.id}
                className={`notif-item ${
                  item.viewed ? "viewed" : ""
                }`}
                onClick={() => markNotificationViewed(item)}
              >
                <img
                  src={
                    info.image ||
                    "https://i.pravatar.cc/100"
                  }
                  alt="client"
                  className="notif-avatar"
                />

                <div className="notif-content">
                  <div className="notif-title-row">
                    <h3 className="notif-name">
                      {info.client_name || "Client"}
                    </h3>
                    {!item.viewed && (
                      <span className="notif-dot" />
                    )}
                  </div>

                  <div className="notif-meta">
                    <div className="notif-meta-item">
                      <IconCalendarEvent className="notif-icon" />
                      <span>
                        {item.appointment_date || "—"}
                      </span>
                    </div>
                    <div className="notif-meta-item">
                      <IconClock className="notif-icon" />
                      <span>
                        {item.appointment_time || "--:--"} –{" "}
                        {item.appointment_end_time || "--:--"}
                      </span>
                    </div>
                  </div>

                  <div className="notif-service">
                    <IconTag className="notif-icon" />
                    <span>
                      {info.service_name || "Service"}
                      {price && ` — ${price}`}
                    </span>
                  </div>

                  <p className="notif-message">
                    {info.service_description ||
                      "No description"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selectedNotification && (
          <motion.div
            className="notif-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() =>
              setSelectedNotification(null)
            }
          >
            <motion.div
              className="notif-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="notif-modal-header">
                <h3>Appointment details</h3>
                <button
                  onClick={() =>
                    setSelectedNotification(null)
                  }
                  className="notif-modal-close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="notif-modal-content">
                <img
                  src={
                    selectedNotification.appointment_info
                      ?.image ||
                    "https://i.pravatar.cc/150"
                  }
                  alt="client"
                  className="notif-modal-avatar"
                />

                <h4>
                  {selectedNotification.appointment_info
                    ?.client_name || "Client"}
                </h4>

                <p className="notif-modal-service">
                  {selectedNotification.appointment_info
                    ?.service_name || "Service"}
                </p>

                <div className="notif-modal-info">
                  <p>
                    <strong>Date:</strong>{" "}
                    {selectedNotification.appointment_date ||
                      "—"}
                  </p>
                  <p>
                    <strong>Time:</strong>{" "}
                    {selectedNotification.appointment_time} –{" "}
                    {
                      selectedNotification.appointment_end_time
                    }
                  </p>
                  <p>
                    <strong>Price:</strong>{" "}
                    {selectedNotification.appointment_info
                      ?.price
                      ? `${getCurrencySymbol(
                          selectedNotification.appointment_info
                            ?.currency || userCurrencyCode
                        )}${selectedNotification.appointment_info.price}`
                      : "—"}
                  </p>
                  <p>
                    <strong>Description:</strong>{" "}
                    {selectedNotification.appointment_info
                      ?.service_description ||
                      "No description"}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}